import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatEngineSizeDisplay } from '../../supabase/functions/_shared/vehicleProfile/engineDisplay';

export interface PensieveComboInsight {
  id: string;
  vehicleLabel: string;
  jobName: string;
  completions: number;
  deltaMinutes: number;
  deltaPercent: number;
  direction: 'faster' | 'slower';
}

export interface TechnicianPensieveStats {
  hasData: boolean;
  combosCount: number;
  totalCompletions: number;
  avgDeltaPercent: number | null;
  strengths: PensieveComboInsight[];
  growthAreas: PensieveComboInsight[];
}

export function useTechnicianPensieveStats(technicianProfileId?: string) {
  return useQuery<TechnicianPensieveStats>({
    queryKey: ['technician-pensieve-stats', technicianProfileId],
    enabled: !!technicianProfileId,
    queryFn: async (): Promise<TechnicianPensieveStats> => {
      const emptyResult: TechnicianPensieveStats = {
        hasData: false,
        combosCount: 0,
        totalCompletions: 0,
        avgDeltaPercent: null,
        strengths: [],
        growthAreas: [],
      };

      if (!technicianProfileId) {
        return emptyResult;
      }

      // Get this tech's shop_technician_ids
      const { data: employment, error: employmentError } = await supabase
        .from('technician_employment_history')
        .select('shop_technician_id')
        .eq('technician_profile_id', technicianProfileId);

      if (employmentError) throw employmentError;

      const techIds = (employment || [])
        .map(e => e.shop_technician_id)
        .filter(Boolean) as string[];

      if (techIds.length === 0) {
        return emptyResult;
      }

      // Pull Pensieve stats for these techIds
      const { data: stats, error: statsError } = await supabase
        .from('pensieve_vehicle_job_stats')
        .select(`
          total_completions,
          avg_actual_minutes,
          job_id,
          vehicle_id
        `)
        .in('tech_id', techIds);

      if (statsError) throw statsError;
      if (!stats || stats.length === 0) {
        return emptyResult;
      }

      // Get unique job and vehicle IDs
      const jobIds = [...new Set(stats.map(s => s.job_id).filter(Boolean))];
      const vehicleIds = [...new Set(stats.map(s => s.vehicle_id).filter(Boolean))];

      // Fetch jobs and vehicles in parallel
      const [jobsResult, vehiclesResult] = await Promise.all([
        supabase.from('jobs').select('id, name, standard_minutes').in('id', jobIds),
        supabase.from('vehicles').select('id, year, make, model, engine_size').in('id', vehicleIds)
      ]);

      if (jobsResult.error) throw jobsResult.error;
      if (vehiclesResult.error) throw vehiclesResult.error;

      const jobsMap = new Map((jobsResult.data || []).map(j => [j.id, j]));
      const vehiclesMap = new Map((vehiclesResult.data || []).map(v => [v.id, v]));

      // Compute deltas vs book per combo
      const combos: PensieveComboInsight[] = [];
      let weightedDeltaSum = 0;
      let totalSamples = 0;

      for (const row of stats) {
        const job = jobsMap.get(row.job_id);
        const vehicle = vehiclesMap.get(row.vehicle_id);
        if (!job || !vehicle || !job.standard_minutes || job.standard_minutes <= 0) continue;

        const avgActual = Number(row.avg_actual_minutes);
        const book = Number(job.standard_minutes);
        if (!avgActual || !book) continue;

        const deltaMinutes = avgActual - book;
        const deltaPercent = ((avgActual / book) - 1) * 100;
        const completions = row.total_completions ?? 0;

        const engineLabel = vehicle.engine_size ? ` ${formatEngineSizeDisplay(vehicle.engine_size)}` : '';
        const vehicleLabel = `${vehicle.year} ${vehicle.make} ${vehicle.model}${engineLabel}`.trim();

        combos.push({
          id: `${vehicle.id}-${job.id}`,
          vehicleLabel,
          jobName: job.name ?? 'Job',
          completions,
          deltaMinutes,
          deltaPercent,
          direction: deltaMinutes <= 0 ? 'faster' : 'slower',
        });

        if (completions > 0) {
          weightedDeltaSum += deltaPercent * completions;
          totalSamples += completions;
        }
      }

      if (combos.length === 0) {
        return emptyResult;
      }

      const avgDeltaPercent = totalSamples > 0 ? weightedDeltaSum / totalSamples : null;

      // Strengths: faster than book by at least 10%, 2+ completions
      const strengths = combos
        .filter(c => c.completions >= 2 && c.deltaPercent <= -10)
        .sort((a, b) => a.deltaPercent - b.deltaPercent)
        .slice(0, 3);

      // Growth areas: slower than book by at least 15%, 2+ completions
      const growthAreas = combos
        .filter(c => c.completions >= 2 && c.deltaPercent >= 15)
        .sort((a, b) => b.deltaPercent - a.deltaPercent)
        .slice(0, 3);

      const totalCompletions = combos.reduce((sum, c) => sum + (c.completions || 0), 0);

      return {
        hasData: true,
        combosCount: combos.length,
        totalCompletions,
        avgDeltaPercent,
        strengths,
        growthAreas,
      };
    },
  });
}
